/*
  Band 0's right end, pinned to the tile's inner edge.

  The top band was reported ending ~20px short of the frame while the
  bottom band reached it. Every measurement available here disagrees - the
  files are ink to their last column, and the rendered ink lands within
  1px of the tile edge at 1280 and 1920 - so rather than argue with a
  screenshot this does the correction the way it was specified, which is
  self-correcting: it measures the ink where it is actually drawn and
  stretches only by the shortfall it finds. Where there is nothing to fix
  the factor is 1 and nothing moves.

  Horizontal only, and only band 0. A few per cent along a cue's length is
  invisible; the same stretch vertically would fatten the stick.

  The scale is about the element's CENTRE, because that is the origin the
  fit pipeline's own transform already assumes and changing it would move
  the band. So reaching the right edge by d costs 2d of scale, half of
  which goes out the left side - where the row's own overflow clips it.
*/
(function () {
  var MIN_FIX = 1;      // px of shortfall worth correcting
  var MAX_STRETCH = 1.12;  // refuse to stretch a cue more than this

  function inkEdges(img, rect) {
    // Ink columns of the image as drawn, mapped back to viewport pixels.
    try {
      var W = Math.max(2, Math.round(rect.width));
      var H = Math.max(2, Math.round(rect.height));
      var c = document.createElement('canvas');
      c.width = W; c.height = H;
      var x = c.getContext('2d', { willReadFrequently: true });
      x.fillStyle = '#fff'; x.fillRect(0, 0, W, H);
      x.drawImage(img, 0, 0, W, H);
      var d = x.getImageData(0, 0, W, H).data, first = -1, last = -1;
      for (var col = 0; col < W; col++) {
        for (var row = 0; row < H; row++) {
          var k = (row * W + col) * 4;
          if ((d[k] + d[k + 1] + d[k + 2]) / 3 < 240) {
            if (first < 0) first = col;
            last = col;
            break;
          }
        }
      }
      if (last < 0) return null;
      return { left: rect.left + first, right: rect.left + last + 1 };
    } catch (e) {
      return null;   // tainted canvas: leave the band alone
    }
  }

  function fitTile(tile) {
    var img = tile.querySelector('.mcd-tile__cue-row img');
    if (!img || !img.naturalWidth) return;
    /*
      A band the inline fit has already decided (snippets/mcd-tile-fit.liquid,
      data-bc-band-sx="fit") is left as it is (2026-09-16). The fit sets the
      same factor the moment the band is fitted, from the ink it measured;
      measuring it again here rounds the drawn band to whole pixels and can
      disagree by a pixel either way - at 375 this found 1.1px short where the
      fit found 0.6, and stretched the butt 2.2px wider 3.5s after it first
      showed. A band the fit could not decide (no size yet, a tainted canvas)
      carries no mark and is still corrected here.
    */
    if (img.getAttribute('data-bc-band-sx') === 'fit') return;
    img.style.removeProperty('--mcd-band-sx');
    var rect = img.getBoundingClientRect();
    if (rect.width < 10) return;
    var ink = inkEdges(img, rect);
    if (!ink) return;
    var cs = window.getComputedStyle(tile);
    var innerRight = tile.getBoundingClientRect().right - parseFloat(cs.borderRightWidth || 0);
    var shortBy = innerRight - ink.right;
    if (shortBy <= MIN_FIX) return;
    var inkWidth = ink.right - ink.left;
    if (inkWidth <= 0) return;
    var k = (inkWidth + 2 * shortBy) / inkWidth;
    if (k > MAX_STRETCH) k = MAX_STRETCH;
    img.style.setProperty('--mcd-band-sx', k.toFixed(4));
  }

  /*
    A section marked data-band-fit="css" (the McDermott rails, 2026-09-17)
    sizes its bands in the stylesheet from files cut to their ink, and its
    band rule ignores --mcd-band-sx; measuring it here would only redo work
    whose answer is already drawn.
  */
  var SECTIONS = '[data-mcdermott-tiles]:not([data-band-fit="css"])';

  function run() {
    var tiles = document.querySelectorAll(SECTIONS + ' .mcd-tile');
    Array.prototype.forEach.call(tiles, fitTile);
  }

  /*
    EVERY band is watched, whatever state it is in.

    This used to read `if (img.complete) return;` before attaching the load
    listener, and an <img> WITH NO src REPORTS complete === true - the trap
    bc-tile-lazy.js documents at length for the same reason. Bands from the
    fifth tile of a row on ship as data-src and get their src later, so they
    were all "complete" here and none of them was ever listened to. The
    MutationObserver on src is not a substitute: it fires when the attribute
    is SET, while naturalWidth is still 0, so fitTile bails and nothing runs
    again once the file lands.

    Measured on the draft at 1920, 2026-09-09, GS and Jump Break rails: the
    first five tiles of each row carried --mcd-band-sx of 1.018-1.120 and
    painted their top band 405.9-417.2px wide; every tile from the sixth on -
    GS07 to GS15, STAR S2 and STAR S86 - carried no sx at all and painted
    364.3px, 10.5% short of the tile's right edge. That is the reported
    "top band cut off on the right at the end of the rail", and it is the
    end of the rail because that is where the deferred tiles are.

    No `once`: the lightbox swaps a band's src in place, and the new band
    needs its own measurement.
  */
  function watch(img) {
    if (img.getAttribute('data-bc-edge-watch')) return;
    img.setAttribute('data-bc-edge-watch', '1');
    img.addEventListener('load', schedule);
  }

  function schedule() {
    if (schedule.frame) return;
    schedule.frame = requestAnimationFrame(function () { schedule.frame = 0; run(); });
  }

  function init() {
    // Every instance of the section, not whichever one happens to carry the
    // literal section id - a second McDermott rail was invisible to this.
    var sections = document.querySelectorAll(SECTIONS);
    if (!sections.length) return;
    var bandImages = [];
    Array.prototype.forEach.call(sections, function (s) {
      Array.prototype.push.apply(bandImages, s.querySelectorAll('.mcd-tile__cue-row img'));
    });
    // Bands load lazily and the lightbox swaps their src on a variant
    // change, so this re-runs rather than measuring once.
    bandImages.forEach(watch);
    if (window.MutationObserver) {
      Array.prototype.forEach.call(sections, function (s) {
        new MutationObserver(schedule).observe(s, {
          subtree: true, attributes: true, attributeFilter: ['src']
        });
      });
    }
    window.addEventListener('resize', schedule, { passive: true });
    schedule();
    window.setTimeout(run, 1200);   // after the lazy images settle
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
